import React, { useEffect, useState } from 'react';
import Toast from '../../../../../components/Toast';
import ButtonLoader from '../../../../../components/ButtonLoader';
import Transition from '../../../../../components/Transition';
import FileUpload from '../../../components/FileUpload';
import FileManager from '../../../components/FileManager';
import { AnimatePresence } from 'framer-motion';
import { ProductData } from '../../../network/api/ProductData';

export default function ProductForm({ product=null, setEditMode }) {
    const [name, setName] = useState(product?.name || '');
    const [slug, setSlug] = useState(product?.slug || '');
    const [description, setDescription] = useState(product?.description || '');
    const [type, setType] = useState(product?.type || 'digital');
    const [paymentType, setPaymentType] = useState(product?.payment_type || 'onceoff');
    const [price, setPrice] = useState(product?.price || '');
    const [duration, setDuration] = useState(product?.duration || '');
    const [status, setStatus] = useState(product?.status || 'active');
    const [metadata, setMetadata] = useState(product?.metadata || '');
    const [visibility, setVisibility] = useState(product?.visibility || 'public');
    const [fileData, setFileData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [imageUrl, setImageUrl] = useState(product?.image || null);
    const [toastConfig, setToastConfig] = useState({
        message: '',
        type: 'primary',
        visible: false,
    });

    const handleSubmit = async () => {
        setIsLoading(true);

        if (!name.trim() || !slug.trim() || !type || !paymentType || !price) {
            setToastConfig({
                message: "All required fields must be filled.",
                type: "danger",
                visible: true,
            });
            setIsLoading(false);
            return;
        }

        try {
            const method = product ? "PUT" : "POST";
            const id = product?.id || null;

            const response = await ProductData({
                method,
                id,
                formData: Object.fromEntries(
                    Object.entries({
                        id,
                        name,
                        slug,
                        description,
                        type,
                        payment_type: paymentType,
                        price,
                        duration,
                        image_id: fileData?.id || null,
                        status,
                        metadata,
                        visibility,
                    }).filter(([k, v]) => v !== null && v !== '')
                ),
            });

            if (response.status === "success") {
                setToastConfig({
                    message: product ? "Product updated successfully!" : "Product created successfully!",
                    type: "success",
                    visible: true,
                });

                if (!product) {
                    setName("");
                    setSlug("");
                    setDescription("");
                    setType("digital");
                    setPaymentType("onceoff");
                    setPrice("");
                    setDuration("");
                    setStatus("active");
                    setMetadata("");
                    setImageUrl(null);
                    setFileData(null);
                    setVisibility("public");
                }
            } else {
                setToastConfig({
                    message: response.message || "Something went wrong.",
                    type: "danger",
                    visible: true,
                });
            }
        } catch (error) {
            setToastConfig({
                message: error.message || "An unexpected error occurred.",
                type: "danger",
                visible: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (fileData?.id) {
            setImageUrl(fileData.file_url);
        }
    }, [fileData]);

    return (
        <AnimatePresence mode="wait">
            <Transition key="product-form">
                <Toast message={toastConfig.message}
                    type={toastConfig.type}
                    visible={toastConfig.visible}
                    setVisible={(visible) => setToastConfig({ ...toastConfig, visible })}
                />

                <div className="row">
                    <div className="col-12">
                        <div className="row">
                            <div className="card custom-card col-lg-8 col-md-8 col-12 ">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <h4 className="card-title">{product ? 'Edit' : 'Create'} Product</h4>
                                    <button className="btn btn-primary btn-sm" onClick={() => setEditMode(false)}>
                                        <i className="fa fa-arrow-left"></i> Back
                                    </button>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        {imageUrl && (
                                            <div className="col-md-4 mb-3 text-center">
                                                <label className="form-label d-block">Image Preview</label>
                                                <img
                                                    src={imageUrl}
                                                    alt="Product Preview"
                                                    className="img-fluid rounded"
                                                    style={{ maxHeight: "200px", objectFit: "contain" }}
                                                />
                                            </div>
                                        )}
                                        <div className="col-md-8">
                                            <div className="mb-3">
                                                <label className="form-label">Product Name</label>
                                                <input type="text" className="form-control" value={name || ''}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setName(val);
                                                        if (!product) {
                                                            setSlug(val.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, ''));
                                                        }
                                                    }} placeholder="Enter product name" />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Slug</label>
                                                <input type="text" className="form-control" value={slug || ''}
                                                    onChange={(e) => setSlug(e.target.value)} placeholder="Slug" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Description</label>
                                        <textarea className="form-control" rows={4} value={description || ''}
                                            onChange={(e) => setDescription(e.target.value)} />
                                    </div>
                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Type</label>
                                            <select className="form-control" value={type || ''}
                                                onChange={(e) => setType(e.target.value)}>
                                                <option value="digital">Digital</option>
                                                <option value="physical">Physical</option>
                                            </select>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Payment Type</label>
                                            <select className="form-control" value={paymentType || ''}
                                                onChange={(e) => setPaymentType(e.target.value)}>
                                                <option value="onceoff">Once-off</option>
                                                <option value="subscription">Subscription</option>
                                            </select>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Status</label>
                                            <select className="form-control" value={status || ''}
                                                onChange={(e) => setStatus(e.target.value)}>
                                                <option value="active">Active</option>
                                                <option value="pending">Pending</option>
                                                <option value="archived">Archived</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Price</label>
                                            <input type="number" className="form-control" value={price || ''}
                                                onChange={(e) => setPrice(e.target.value)} />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Duration (days)</label>
                                            <input type="number" className="form-control" value={duration || ''}
                                                onChange={(e) => setDuration(e.target.value)} />
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Visibility</label>
                                        <select className="form-control" value={visibility || ''} onChange={(e) => setVisibility(e.target.value)}>
                                            <option value="public">Public</option>
                                            <option value="hidden">Hidden</option>
                                            <option value="app_only">App Only</option>
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Metadata (JSON)</label>
                                        <textarea className="form-control" value={metadata || ''}
                                            onChange={(e) => setMetadata(e.target.value)} />
                                    </div>
                                    <button className="btn btn-primary mt-2" onClick={handleSubmit} disabled={isLoading}>
                                        {isLoading ? <ButtonLoader /> : <><i className="fa fa-save me-2"></i>Save Product</>}
                                    </button>
                                </div>
                            </div>
                            <div className=" col-lg-4 col-md-4 ">
                                <div className="row">
                                    <div className="col-12 ">
                                        <FileManager fileData={fileData} setFileData={setFileData} />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className=" col-12 mb-3">
                                        <div className="card">
                                            <div className="card-body">
                                                <h4 className="card-title">Upload Files</h4>
                                                <p className="text-muted">Upload files to the article. You can upload images, videos, and documents.</p>
                                                <FileUpload mode="image" setFileData={setFileData} description={"Product Image"} />
                                            </div>
                                        </div>

                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
        </AnimatePresence>
    );
}